export interface User {
  address: string;
  admin_id: any;
  affiliation_code?: string;
  app_source?: string;
  bank_account: BankAccount[];
  has_bank_account: boolean;
  birth_date: string;
  bonus_enabled: number;
  cancelled_account: number;
  city: string;
  commission_grid_id: any;
  commission: number;
  country_data: CountriesList;
  country: string;
  created_at: string;
  currency: string;
  ddi: string;
  document: Document;
  email_verified_at: any;
  email: string;
  first_name: string;
  ftd_date: any;
  ftd_value: number;
  /**
   *  * Usuário dispensado das validações do primeiro depósito por vir de campanha de
   * CRM — TODOS os módulos (email, phone, address, kyc), não só o KYC. Decidido pela
   * API (FtdValidationExemptionService); o front apenas consome, para não exigir uma
   * verificação que o depósito não vai pedir.
   */
  ftd_validation_exempt?: boolean;
  gender: number;
  id: number;
  is_active: number;
  language: string;
  last_login: string;
  last_name: string;
  latestKyc?: Kyc.Data;
  manager_id: any;
  menu_list: any;
  mother_name: string;
  name: string;
  phone: string;
  recommended_by: any;
  roles: any;
  showPendingDataFlow: boolean;
  state: string;
  timezone: string;
  token: string;
  updated_at: string;
  user_validates: UserValidate[];
  userInfo: UserInfo;
  username: string;
  wallet: Wallet;
  zipcode: string;
}

export type ForceRequestKycReasons =
  | 'password_changed'
  | 'reset_password'
  | 'admin_change_password'
  | 'location_changed_login'
  | 'device_changed_login'
  | 'ip_changed'
  | 'login_7_days_ago';

export interface UserInfo {
  validate_email_at: string;
  validate_sms_at: string;
  latitude: number;
  longitude: number;
  location_at: string;
  login_at: string;
  login_attempt_qty: number;
  seven_days_since_last_login: boolean;
  nationalities: string;
  strong_password: number;
  force_request_kyc: boolean;
  force_request_kyc_reasons: ForceRequestKycReasons[];
  mkt_accepted_at: string;
  tc_accepted_at: string;
  lgpd_accepted_at: string;
  law_accepted_at: string;
  privacy_accepted_at: string;
  migrate_accepted_at: string;
  two_factor_at: string | null;
  two_factor_code: string | null;
  two_factor_enabled: boolean;
  two_factor_type?: 'email' | 'sms';
}

export interface UserValidate {
  created_at: string;
  ext_person_id: number;
  id: number;
  payload: any;
  status: string;
  updated_at: string;
  user_id: number;
}

export interface CountriesList {
  id: number;
  code: string;
  ddi: string;
  currency?: string;
  name?: string;
  alpha2?: string;
}

export interface UpdateUser {
  captcha_token: string;
  kyc_id?: number;
  password_check?: string;
  state?: string;
  zipcode?: string;
  city?: string;
  address?: string;
  phone?: string;
  ddi?: string;
  affiliation_code?: string;
  email?: string;
  birth_date?: string;
  terms?: boolean;
  language?: string;
  timezone?: string;
  country?: string;
  src?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
}

export interface UpdateUserEmail {
  kyc_id?: number;
  password_check?: string;
  email: string;
}

export interface Pivot {
  model_id: number;
  role_id: number;
  model_type: string;
}

export interface Wallet {
  id: number;
  balance: number;
  bonus: number;
  credit: number;
  available_value: number;
  min_withdraw_amount: number | null;
  max_withdraw_amount: number | null;
  user_id: number;
  withdraw_enabled: number;
  created_at: string;
  updated_at: string;
}

export interface Document {
  id: number;
  number: string;
  type: string;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface TypeUsersStore {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface UserDocument {
  docnumber?: string;
  docnumber_valid?: boolean;
  birth_date?: string;
  gender?: string;
  mother_name?: string;
  name?: string;
}

export interface RecoveryPasswordData {
  code?: string;
  kyc_id?: number;
  login?: string; // email, cpf
}

export interface RecoveryPasswordFlow {
  token: string;
  email: string | null;
  final_phone: string | null;
  kyc: boolean;
  active_options: {
    feature_kyc_password_recovery: boolean;
    feature_email_password_recovery: boolean;
    feature_phone_password_recovery: boolean;
  };
}

export interface ContactValidationFlow {
  email: string | null;
  final_phone: string | null;
  kyc: boolean;
  contact_validated: boolean;
  active_options: {
    email: boolean;
    phone: boolean;
  };
}

export type VisitorCountryTest =
  | 'BRA'
  | 'CHL'
  | 'IND'
  | 'MEX'
  | 'PRY'
  | 'ARG'
  | 'ESP'
  | 'ITA'
  | 'USA';

export enum BankAccountType {
  CORRENTE = 'CORRENTE',
  POUPANCA = 'POUPANCA',
  SALARIO = 'SALARIO',
}

// Enum separado do BankAccountType (usado por auth/verification/BankAccount.vue,
// outro endpoint/contrato) pra não acoplar os dois fluxos.
export enum ManualBankAccountType {
  CORRENTE = 'CORRENTE',
  POUPANCA = 'POUPANCA',
}

export interface Visitor {
  country: string;
  country_alpha2?: string;
}

export interface BankAccount {
  kyc_id?: number;
  password_check?: string;
  agency: string;
  agency_digit: string;
  bank_id: number;
  bank_name?: string;
  cnpj?: string;
  cpf?: string;
  id?: number;
  key_pix: string;
  number: string;
  number_digit: string;
  type:
    | BankAccountType.CORRENTE
    | BankAccountType.POUPANCA
    | BankAccountType.SALARIO;
  type_pix: 'CPF' | 'PHONE' | 'EMAIL' | 'RANDOM';
}

export interface UpdatePixKey {
  pix_key_value: string;
  pix_key_type: string;
  phone_number?: string;
  phone_ddi?: string;
  password_check?: string;
  kyc_id?: number;
}

export interface PixKeyData {
  value: string;
  type: string;
  bank_code?: string;
}

export interface MexBankAccountData {
  type: string;
  number: string;
  bank_code: string;
}
